import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useMapVisibility } from '../SimViewer/useMapVisibility';
import {
  useAggregationConfig,
  type AggregationTarget,
  type AggregationSubTarget,
  type AggregationType,
} from '../SimViewer/useAggregationConfig';
import type { AreaAggregationType } from '../../infrastructure/dataApi/client';
import type { AreaAggregation } from '../../types/domain';

type UseAreaAggregationData = {
  isLoading: boolean;
  aggregations: Map<string, number>;
};

const EMPTY_DATA = new Map<string, never>();

const getAggregationType = (
  target: AggregationTarget,
  subTarget: AggregationSubTarget | undefined,
  type: AggregationType
): AreaAggregationType | null => {
  switch (target) {
    case 'home':
      switch (type) {
        case 'areas':
          return 'home_agents_per_unit_area';
        case 'area_pref':
          return 'home_pref_agents_per_unit_area';
        default:
          return null;
      }
    case 'work':
      switch (type) {
        case 'areas':
          return 'work_agents_per_unit_area';
        default:
          return null;
      }
    case 'trip_start':
      switch (type) {
        case 'areas':
          return 'start_trips_per_unit_area';
        default:
          return null;
      }
    case 'trip_end':
      switch (type) {
        case 'areas':
          return 'end_trips_per_unit_area';
        default:
          return null;
      }
    case 'traveled_distance':
      switch (type) {
        case 'areas':
          switch (subTarget) {
            case 'home':
              return 'home_traveled_distance_avg';
            case 'work':
              return 'work_traveled_distance_avg';
            case 'trip_start':
              return 'start_traveled_distance_avg';
            case 'trip_end':
              return 'end_traveled_distance_avg';
            default:
              return null;
          }
        default:
          return null;
      }
    case 'traveled_time':
      switch (type) {
        case 'areas':
          switch (subTarget) {
            case 'home':
              return 'home_traveled_time_avg';
            case 'work':
              return 'work_traveled_time_avg';
            case 'trip_start':
              return 'start_traveled_time_avg';
            case 'trip_end':
              return 'end_traveled_time_avg';
            default:
              return null;
          }
        default:
          return null;
      }
    case 'activity_time':
      switch (type) {
        case 'areas':
          switch (subTarget) {
            case 'home':
              return 'home_activity_time_avg';
            case 'work':
              return 'work_activity_time_avg';
            case 'trip_start':
              return 'start_activity_time_avg';
            case 'trip_end':
              return 'end_activity_time_avg';
            default:
              return null;
          }
        default:
          return null;
      }
    default:
      return null;
  }
};

const useAreaAggregationData = (
  type?: AreaAggregationType
): UseAreaAggregationData => {
  const { visibleAreaType } = useMapVisibility();
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();

  const aggregationType =
    type ??
    (aggregationConfig
      ? getAggregationType(
          aggregationConfig.target,
          aggregationConfig.subTarget,
          aggregationConfig.type
        )
      : null);
  const isRequired = aggregationType !== null;

  const { data, isFetching } = useQuery({
    queryKey: [
      'areaAggregation',
      currentSimDataId,
      apiFilter,
      aggregationType,
      visibleAreaType,
    ],
    queryFn:
      currentSimDataId && isRequired
        ? async ({
            signal,
          }: QueryFunctionContext): Promise<Map<string, number>> => {
            return dataApiClient
              .getAreaAggr(
                currentSimDataId,
                apiFilter,
                aggregationType,
                visibleAreaType,
                signal
              )
              .then((items: AreaAggregation[]): Map<string, number> => {
                return new Map<string, number>(
                  items.map((item: AreaAggregation): [string, number] => {
                    return [item.key_code, item.value];
                  })
                );
              });
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: type ? 'others' : 'aggregation' },
  });

  return {
    isLoading: isFetching,
    aggregations: data && isRequired ? data : EMPTY_DATA,
  };
};

export { useAreaAggregationData };
